import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useSupport } from "../../state/supportContext.jsx";

export function SelectSupportDrawer({ open, onClose }) {
  const { setSellerSlug, support } = useSupport();
  const [step, setStep] = useState("group"); // 'group' | 'seller'
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [sellersError, setSellersError] = useState(null);

  // Debounce för sök
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(id);
  }, [query]);

  // Hämta grupper
  useEffect(() => {
    if (!open) return;
    setGroupsLoading(true);
    setGroupsError(null);

    api
      .searchGroups(debouncedQuery || "")
      .then((data) => {
        setGroups(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setGroupsError("Kunde inte hämta grupper just nu.");
      })
      .finally(() => setGroupsLoading(false));
  }, [debouncedQuery, open]);

  // Hämta säljare när grupp valts
  useEffect(() => {
    if (!selectedGroup || !open) return;
    setSellersLoading(true);
    setSellersError(null);
    api
      .getGroupSellers(selectedGroup.id)
      .then((data) => {
        setSellers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setSellersError("Kunde inte hämta säljare för den här gruppen.");
      })
      .finally(() => setSellersLoading(false));
  }, [selectedGroup, open]);

  // Nollställ state när panelen stängs
  useEffect(() => {
    if (!open) {
      setStep("group");
      setQuery("");
      setDebouncedQuery("");
      setSelectedGroup(null);
      setGroups([]);
      setGroupsError(null);
      setSellers([]);
      setSellersError(null);
    }
  }, [open]);

  const handleChooseGroup = (group) => {
    setSelectedGroup(group);
    setStep("seller");
  };

  const handleChooseSeller = (seller) => {
    if (!seller?.slug) return;
    setSellerSlug(seller.slug);
    onClose?.();
  };


  const hasActiveSupport = !!support;

  const title = useMemo(() => {
    if (step === "seller" && selectedGroup) {
      return `Välj säljare i ${selectedGroup.name}`;
    }
    return "Välj vem du vill stötta";
  }, [step, selectedGroup]);

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        <header className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-xs font-semibold tracking-wide text-primary-600 uppercase">
              Stötta en grupp
            </p>
            <h2 className="text-lg font-semibold text-neutral-900">{title}</h2>
            {hasActiveSupport && support && (
              <p className="text-xs text-neutral-500">
                Du stöttar just nu{" "}
                <span className="font-medium">
                  {support.groupName} – {support.sellerName}
                </span>
                .
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100"
            aria-label="Stäng"
          >
            <span className="sr-only">Stäng</span>
            <svg
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 6L14 14M6 14L14 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {step === "group" && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700">
                  Sök efter förening, lag eller klass
                </label>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Exempel: Borås HC U10"
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-neutral-500">
                  Du kan också lämna fältet tomt för att se ett urval av aktiva grupper.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="text-xs font-semibold tracking-wide text-neutral-500 uppercase">
                  Aktiva grupper
                </h3>
                {groupsLoading && (
                  <p className="text-sm text-neutral-500">Laddar grupper…</p>
                )}
                {groupsError && (
                  <p className="text-sm text-rose-600">{groupsError}</p>
                )}
                {!groupsLoading && !groupsError && groups.length === 0 && (
                  <p className="text-sm text-neutral-500">
                    Inga grupper matchade din sökning.
                  </p>
                )}
                <ul className="space-y-3">
                  {groups.map((group) => (
                    <li
                      key={group.id}
                      className="border border-neutral-200 rounded-xl p-3 flex items-center justify-between gap-3 hover:border-primary-400 hover:bg-primary-50/40 transition-colors"
                    >
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium text-neutral-900">
                          {group.name}
                        </p>
                        {group.city && (
                          <p className="text-xs text-neutral-500">{group.city}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleChooseGroup(group)}
                        className="px-3 py-1.5 rounded-full bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700"
                      >
                        VÄLJ
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {step === "seller" && selectedGroup && (
            <>
              <button
                type="button"
                onClick={() => setStep("group")}
                className="text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1"
              >
                <span>←</span>
                <span>Tillbaka till grupper</span>
              </button>

              <div className="space-y-1">
                <p className="text-sm font-medium text-neutral-900">
                  {selectedGroup.name}
                </p>
                {selectedGroup.city && (
                  <p className="text-xs text-neutral-500">
                    {selectedGroup.city}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <h3 className="text-xs font-semibold tracking-wide text-neutral-500 uppercase">
                  Välj säljare
                </h3>
                {sellersLoading && (
                  <p className="text-sm text-neutral-500">Laddar säljare…</p>
                )}
                {sellersError && (
                  <p className="text-sm text-rose-600">{sellersError}</p>
                )}
                {!sellersLoading && !sellersError && sellers.length === 0 && (
                  <p className="text-sm text-neutral-500">
                    Inga säljare hittades för den här gruppen ännu.
                  </p>
                )}
                <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {sellers.map((seller) => (
                    <li
                      key={seller.id}
                      className="border border-neutral-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3 hover:border-primary-400 hover:bg-primary-50/40 transition-colors"
                    >
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {seller.name}
                        </p>
                        {seller.meta?.class && (
                          <p className="text-xs text-neutral-500">
                            {seller.meta.class}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleChooseSeller(seller)}
                        className="px-3 py-1.5 rounded-full bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700"
                      >
                        Stötta {seller.name.split(" ")[0]}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

            </>
          )}
        </div>
      </div>
    </>
  );
}

export default SelectSupportDrawer;


